import { Dictionary, NumericDictionary, orderBy } from 'lodash';

import { SortByOption } from 'models/SortByOptions';

import { BooleanMap } from './typeHelpers';

/**
 * @template T - Type of array elements
 * @template V - Option value type, i.e. the literal key by which to sort
 */
export function sort<T, V>({
  items,
  sortOptions,
  sortBy,
}: {
  items: T[];
  sortOptions: SortByOption<T, V>[];
  sortBy: V;
}): T[] {
  const sortOption = sortOptions.find(so => so.value === sortBy);
  if (!sortOption) return items;
  return orderBy(items, sortOption.keys, sortOption.orders);
}

export function hasIntersection<T = any>(array1: T[], array2: T[]) {
  const array2Set = new Set(array2);
  return !!array1.find(value => array2Set.has(value));
}

export function mapToDictionary<T, T2>(
  array: T[],
  funcKeySelector: (t: T) => string,
  funcValSelector: (t: T) => T2
): Dictionary<T2> {
  return (array || []).reduce(
    (acc, x) => ({
      ...acc,
      [funcKeySelector(x)]: funcValSelector(x),
    }),
    {} as Dictionary<T2>
  );
}
export function mapToNumericDictionary<T, T2>(
  array: T[],
  funcKeySelector: (t: T) => number,
  funcValSelector: (t: T) => T2
): NumericDictionary<T2> {
  return (array || []).reduce(
    (acc, x) => ({
      ...acc,
      [funcKeySelector(x)]: funcValSelector(x),
    }),
    {} as NumericDictionary<T2>
  );
}

/**
 * Goes through the keys of object `a` (1 level/shallow), comparing them with keys/values of `b`
 * Returns boolean true for each key different between `a` and `b` as a dictionary.
 * NOTE: `!==` is used for equality comparison
 * Example: { x: 1, y: 2 } vs { x: 1, y: 3 } returns { y: true }
 */
export function getKeysWithDifferentValues<T>(a: T, b: T): BooleanMap<T> {
  const diff = {};
  Object.keys(a).forEach(k => {
    if (a[k] !== b[k]) diff[k] = true;
  });
  Object.keys(b).forEach(k => {
    if (a[k] !== b[k]) diff[k] = true;
  });
  return diff;
}

/**
 * Goes through an object's keys and finds the key with the first matching value
 * @param obj the object to search
 * @param value the value to match
 */
export function getKeyOfFirstInstanceOfValue(obj: object, value) {
  if (!obj) return null;
  if (!Object.keys(obj).length) return null;

  return Object.keys(obj).find(key => obj[key] === value) ?? null;
}

/**
 * Takes an object array and a key to get unique values from and returns an array of unique values
 * EG: [{ a: 1, b: 3}, { a: 2, b: 6}, { a: 1, b: 6} ] where key = a, returns [1,2]
 * @param objArr The array to get a unique val from
 * @param objKey The key to compare values
 */
export function getUniqueValArrFromObjArr(objArr: object[], objKey: string) {
  if (!objArr?.length || !objKey?.length) return [];
  return Array.from(new Set(objArr.map((item: object) => item[objKey])));
}

/**
 * Flatten an object's entries into an array
 * NOTE: This function is not recursive and only gets the first level of values
 * @param obj The object to flatten
 */
export function getValArrayFromObj(obj: object) {
  if (!obj || !Object.values(obj).length) return [];
  return Object.values(obj).map(d => d);
}
